import { useCallback, useEffect, useMemo, useState } from 'react'

import { IncomeCalcLocation } from '../models/IncomeCalcLocation.model'
import { USStates } from '../models/USStates.model'
import { IncomeCalcService } from '../services/IncomeCalcService/IncomeCalcService'
import { IntRange } from '../utils'
import { resolveCityFromQuery, syncCityFieldsWithLocations } from '../utils/calculatorCity'
import { getIncomeCalcErrorMessage } from '../utils/calculatorErrors'
import { clampDayToMonth, getDaysInMonth } from '../utils/calendarDate'
import { clampToValidDate, getValidMonthsForYear, getValidYearsForMonth } from '../utils/gsaYearRange'

export type CalculatorDateValues = {
  startMonth: IntRange<1, 13>
  startDay: number
  startYear: number
}

export type CalculatorLocationFormValues = CalculatorDateValues & {
  state: USStates
  cityQuery: string
  selectedCity: string
}

const getInitialDateValues = (): CalculatorDateValues => {
  const today = new Date()
  const clamped = clampToValidDate(today.getMonth() + 1, today.getDate(), today.getFullYear())
  return {
    startDay: clamped.day,
    startMonth: clamped.month as IntRange<1, 13>,
    startYear: clamped.year,
  }
}

export const useCalculatorForm = () => {
  const incomeCalcService = useMemo(() => new IncomeCalcService(), [])
  const [fieldValues, setFieldValues] = useState<CalculatorLocationFormValues>(() => ({
    ...getInitialDateValues(),
    cityQuery: '',
    selectedCity: '',
    state: USStates.Alabama,
  }))
  const [locations, setLocations] = useState<IncomeCalcLocation[]>([])
  const [locationsLoading, setLocationsLoading] = useState(false)
  const [locationsError, setLocationsError] = useState('')
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const validMonths = useMemo(() => getValidMonthsForYear(fieldValues.startYear), [fieldValues.startYear])
  const validYears = useMemo(() => getValidYearsForMonth(fieldValues.startMonth), [fieldValues.startMonth])
  const validDays = useMemo(
    () => getDaysInMonth(fieldValues.startMonth, fieldValues.startYear),
    [fieldValues.startMonth, fieldValues.startYear],
  )

  const filteredLocations = useMemo(() => {
    const query = fieldValues.cityQuery.trim().toLowerCase()
    if (!query) {
      return locations
    }
    return locations.filter((location) => location.city.toLowerCase().includes(query))
  }, [fieldValues.cityQuery, locations])

  const isDateValid =
    validMonths.includes(fieldValues.startMonth) &&
    validYears.includes(fieldValues.startYear) &&
    fieldValues.startDay >= 1 &&
    fieldValues.startDay <= validDays

  const resolvedCity = useMemo(() => {
    if (fieldValues.selectedCity) {
      return fieldValues.selectedCity
    }
    return resolveCityFromQuery(fieldValues.cityQuery, locations)
  }, [fieldValues.selectedCity, fieldValues.cityQuery, locations])

  const isCityValid = resolvedCity !== null
  const canSubmit = isDateValid && isCityValid && !locationsLoading

  useEffect(() => {
    if (!isDateValid) {
      setLocations([])
      setLocationsError('')
      return
    }

    let cancelled = false
    setLocationsLoading(true)
    setLocationsError('')

    incomeCalcService
      .getLocations({
        startMonth: fieldValues.startMonth,
        startYear: fieldValues.startYear,
        state: fieldValues.state,
      })
      .then((nextLocations) => {
        if (cancelled) {
          return
        }
        setLocations(nextLocations)

        let cityDropped = false
        setFieldValues((prev) => {
          const previousCity = prev.selectedCity || prev.cityQuery.trim()
          const synced = syncCityFieldsWithLocations(prev.cityQuery, prev.selectedCity, nextLocations)
          cityDropped = Boolean(previousCity && !synced.selectedCity)

          if (synced.cityQuery === prev.cityQuery && synced.selectedCity === prev.selectedCity) {
            return prev
          }

          return { ...prev, ...synced }
        })

        if (cityDropped) {
          setValidationErrors((prev) => ({ ...prev, city: 'Select a valid city from the list.' }))
        } else {
          setValidationErrors((prev) => {
            if (!prev.city) {
              return prev
            }
            const next = { ...prev }
            delete next.city
            return next
          })
        }
      })
      .catch((error) => {
        if (cancelled) {
          return
        }
        setLocations([])
        const { message } = getIncomeCalcErrorMessage(error)
        setLocationsError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setLocationsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [fieldValues.state, fieldValues.startMonth, fieldValues.startYear, incomeCalcService, isDateValid])

  const updateMonth = useCallback((startMonth: number) => {
    setFieldValues((prev) => {
      const validYearsForMonth = getValidYearsForMonth(startMonth)
      let startYear = prev.startYear
      if (!validYearsForMonth.includes(startYear)) {
        startYear = validYearsForMonth[validYearsForMonth.length - 1] ?? startYear
      }
      const validMonthsForYear = getValidMonthsForYear(startYear)
      let nextMonth = startMonth
      if (!validMonthsForYear.includes(nextMonth)) {
        nextMonth = validMonthsForYear[validMonthsForYear.length - 1] ?? nextMonth
      }
      const startDay = clampDayToMonth(nextMonth, prev.startDay, startYear)
      return {
        ...prev,
        startDay,
        startMonth: nextMonth as IntRange<1, 13>,
        startYear,
      }
    })
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next['start-date']
      return next
    })
  }, [])

  const updateDay = useCallback((startDay: number) => {
    setFieldValues((prev) => ({
      ...prev,
      startDay: clampDayToMonth(prev.startMonth, startDay, prev.startYear),
    }))
  }, [])

  const updateYear = useCallback((startYear: number) => {
    setFieldValues((prev) => {
      const validMonthsForYear = getValidMonthsForYear(startYear)
      let startMonth: number = prev.startMonth
      if (!validMonthsForYear.includes(startMonth)) {
        startMonth = validMonthsForYear[validMonthsForYear.length - 1] ?? startMonth
      }
      const validYearsForMonth = getValidYearsForMonth(startMonth)
      let nextYear = startYear
      if (!validYearsForMonth.includes(nextYear)) {
        nextYear = validYearsForMonth[validYearsForMonth.length - 1] ?? nextYear
      }
      const startDay = clampDayToMonth(startMonth, prev.startDay, nextYear)
      return {
        ...prev,
        startDay,
        startMonth: startMonth as IntRange<1, 13>,
        startYear: nextYear,
      }
    })
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next['start-date']
      return next
    })
  }, [])

  const updateState = useCallback((state: USStates) => {
    setFieldValues((prev) => ({
      ...prev,
      cityQuery: '',
      selectedCity: '',
      state,
    }))
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next.city
      return next
    })
  }, [])

  const updateCityQuery = useCallback((cityQuery: string) => {
    setFieldValues((prev) => ({
      ...prev,
      cityQuery,
      selectedCity: '',
    }))
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next.city
      return next
    })
  }, [])

  const selectCity = useCallback((city: string) => {
    setFieldValues((prev) => ({
      ...prev,
      cityQuery: city,
      selectedCity: city,
    }))
    setValidationErrors((prev) => {
      const next = { ...prev }
      delete next.city
      return next
    })
  }, [])

  const validateCityOnBlur = useCallback(() => {
    const query = fieldValues.cityQuery.trim()

    setValidationErrors((prev) => {
      const next = { ...prev }

      if (!query) {
        delete next.city
        return next
      }

      const matchedCity = resolveCityFromQuery(query, locations)
      if (matchedCity) {
        delete next.city
        return next
      }

      next.city = 'Select a valid city from the list.'
      return next
    })

    const matchedCity = resolveCityFromQuery(query, locations)
    if (matchedCity) {
      setFieldValues((prev) => ({
        ...prev,
        cityQuery: matchedCity,
        selectedCity: matchedCity,
      }))
    }
  }, [fieldValues.cityQuery, locations])

  const validateLocationForm = useCallback(() => {
    const nextErrors: Record<string, string> = {}

    if (!isDateValid) {
      nextErrors['start-date'] = 'Rates are not available for that date.'
    }

    const matchedCity = resolveCityFromQuery(fieldValues.cityQuery, locations)
    const city = fieldValues.selectedCity || matchedCity

    if (matchedCity && !fieldValues.selectedCity) {
      setFieldValues((prev) => ({
        ...prev,
        cityQuery: matchedCity,
        selectedCity: matchedCity,
      }))
    }

    if (!city) {
      nextErrors.city = 'Select a valid city from the list.'
    }

    setValidationErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }, [fieldValues.cityQuery, fieldValues.selectedCity, isDateValid, locations])

  const clearValidationError = useCallback((field: string) => {
    setValidationErrors((prev) => {
      if (!prev[field]) {
        return prev
      }
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  return {
    canSubmit,
    clearValidationError,
    fieldValues,
    filteredLocations,
    incomeCalcService,
    isCityValid,
    isDateValid,
    locations,
    locationsError,
    locationsLoading,
    resolvedCity,
    selectCity,
    setValidationErrors,
    updateCityQuery,
    updateDay,
    updateMonth,
    updateState,
    updateYear,
    validDays,
    validMonths,
    validYears,
    validateCityOnBlur,
    validateLocationForm,
    validationErrors,
  }
}
